// Diferencias entre el prompt actual y la propuesta del asistente. Se compara por líneas y cada grupo de líneas distintas es un
// «cambio» que la persona acepta o rechaza por separado; el texto final se arma con lo que aceptó. Sin dependencias.

const MAX_CELLS = 4_000_000;   // líneas × líneas: por encima, un solo cambio grande en lugar de un cálculo pesado

const splitLines = (t) => (t === '' ? [] : String(t).replace(/\r\n/g, '\n').split('\n'));

/** Sucesión de operaciones {type: 'eq' | 'del' | 'add', line}. */
export function diffLines(a, b) {
  const x = splitLines(a); const y = splitLines(b);
  let start = 0;
  while (start < x.length && start < y.length && x[start] === y[start]) start++;
  let endX = x.length; let endY = y.length;
  while (endX > start && endY > start && x[endX - 1] === y[endY - 1]) { endX--; endY--; }
  const head = x.slice(0, start).map((line) => ({ type: 'eq', line }));
  const tail = x.slice(endX).map((line) => ({ type: 'eq', line }));
  const mx = x.slice(start, endX); const my = y.slice(start, endY);
  if (!mx.length && !my.length) return [...head, ...tail];
  if (mx.length * my.length > MAX_CELLS) return [...head, ...mx.map((line) => ({ type: 'del', line })), ...my.map((line) => ({ type: 'add', line })), ...tail];
  const n = mx.length; const m = my.length;
  const w = m + 1;
  const lcs = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) lcs[i * w + j] = mx[i] === my[j] ? lcs[(i + 1) * w + j + 1] + 1 : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1]);
  const mid = [];
  let i = 0; let j = 0;
  while (i < n && j < m) {
    if (mx[i] === my[j]) { mid.push({ type: 'eq', line: mx[i] }); i++; j++; }
    else if (lcs[(i + 1) * w + j] >= lcs[i * w + j + 1]) { mid.push({ type: 'del', line: mx[i] }); i++; }
    else { mid.push({ type: 'add', line: my[j] }); j++; }
  }
  while (i < n) mid.push({ type: 'del', line: mx[i++] });
  while (j < m) mid.push({ type: 'add', line: my[j++] });
  return [...head, ...mid, ...tail];
}

/** Agrupa las operaciones en tramos: {kind: 'eq', lines} o {kind: 'hunk', id, del: [...], add: [...]}. */
export function toSegments(ops) {
  const segs = [];
  let id = 0;
  for (const op of ops) {
    const last = segs[segs.length - 1];
    if (op.type === 'eq') {
      if (last?.kind === 'eq') last.lines.push(op.line); else segs.push({ kind: 'eq', lines: [op.line] });
    } else {
      let h = last?.kind === 'hunk' ? last : null;
      if (!h) { h = { kind: 'hunk', id: id++, del: [], add: [] }; segs.push(h); }
      (op.type === 'del' ? h.del : h.add).push(op.line);
    }
  }
  return segs;
}

export const hunksOf = (segs) => segs.filter((s) => s.kind === 'hunk');

/** El texto con los cambios aceptados (`accepted`: Set de ids de cambio); los demás se quedan como estaban. */
export function applyHunks(segs, accepted) {
  const out = [];
  for (const s of segs) {
    if (s.kind === 'eq') out.push(...s.lines);
    else out.push(...(accepted.has(s.id) ? s.add : s.del));
  }
  return out.join('\n');
}

/** Palabras que cambian entre dos líneas, para resaltarlas dentro de la línea. Devuelve {del: [{t, hl}], add: [{t, hl}]}. */
export function wordDiff(a, b) {
  const tok = (s) => s.match(/\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]/gu) ?? [];   // palabras, espacios y cada signo por separado
  const x = tok(a); const y = tok(b);
  if (x.length * y.length > 40_000) return { del: [{ t: a, hl: true }], add: [{ t: b, hl: true }] };
  const n = x.length; const m = y.length; const w = m + 1;
  const l = new Uint16Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) l[i * w + j] = x[i] === y[j] ? l[(i + 1) * w + j + 1] + 1 : Math.max(l[(i + 1) * w + j], l[i * w + j + 1]);
  const del = []; const add = [];
  const push = (arr, t, hl) => { const k = arr[arr.length - 1]; if (k && k.hl === hl) k.t += t; else arr.push({ t, hl }); };
  let i = 0; let j = 0;
  while (i < n && j < m) {
    if (x[i] === y[j]) { push(del, x[i], false); push(add, y[j], false); i++; j++; }
    else if (l[(i + 1) * w + j] >= l[i * w + j + 1]) push(del, x[i++], true);
    else push(add, y[j++], true);
  }
  while (i < n) push(del, x[i++], true);
  while (j < m) push(add, y[j++], true);
  return { del, add };
}
